import React, { useState } from "react";

import css from "./ColorSelect.module.css";

import chevronDownSvg from "public/assets/svg/chevron-down.svg";
import chevronUpSvg from "public/assets/svg/chevron-up.svg";

import cn from "classnames";

import Trigger from "@rc-component/trigger";
import { Color } from "three";

type ColorSelectProps = {
    colors: Color[];
    onChange: (color: Color) => void;
    selection: Color[];
};

export const ColorSelect: React.FC<ColorSelectProps> = ({ colors, onChange, selection }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Trigger
            action={["click"]}
            popup={<ColorPalette colors={colors} onChange={onChange} selection={selection} />}
            popupAlign={{
                points: ["tr", "br"],
                offset: [0, 10],
            }}
            autoDestroy
            onOpenChange={setIsOpen}
        >
            <div className={css.main}>
                <ColorView color={selection} onClick={() => setIsOpen(true)} size="big" />
                <img src={isOpen ? chevronUpSvg : chevronDownSvg} alt="chevron-down" />
            </div>
        </Trigger>
    );
};

type ColorViewProps = {
    color: Color | Color[];
    isSelected?: boolean;
    onClick: () => void;
    size?: "big" | "large" | "small";
};

export const ColorView: React.FC<ColorViewProps> = ({
    color,
    isSelected,
    onClick,
    size = "small",
}) => {
    const colors = Array.isArray(color) ? color : [color];

    return (
        <div
            className={cn(css.colorView, isSelected && css.selected, size !== "small" && css[size])}
            onClick={onClick}
        >
            <div className={css.colorViewInner}>
                {colors.map((color, index) => (
                    <div
                        key={index}
                        className={cn(
                            css.colorViewInnerItem,
                            colors.length === 2 && css.colors2,
                            colors.length > 2 && css.colorsN
                        )}
                        style={
                            {
                                backgroundColor: "#" + color.getHexString(),
                                "--index": index,
                                "--count": colors.length,
                            } as React.CSSProperties
                        }
                    ></div>
                ))}
            </div>
        </div>
    );
};

type ColorPaletteProps = {
    colors: Color[];
    onChange: (color: Color) => void;
    selection: Color[];
    ref: React.RefObject<HTMLDivElement>;
};

const ColorPalette = React.forwardRef<HTMLDivElement, ColorPaletteProps>(
    ({ colors, onChange, selection }, ref) => {
        return (
            <div className={css.colorPalette} ref={ref}>
                {colors.map((color, index) => (
                    <ColorView
                        key={index}
                        color={color}
                        isSelected={selection.includes(color)}
                        onClick={() => onChange(color)}
                    />
                ))}
            </div>
        );
    }
);

ColorPalette.displayName = "ColorPalette";
